import type { ChecklistItem, RankedChecklistMatch } from '../../config/documentChecklists';
import { REQUIREMENT_TO_SECTION_MAP } from '../../config/requirementToSection';
import type { SiteDocument } from '../../types/siteRag';
import type { EvidenceSection, SiteEvidenceDocument, StructuredEvidenceValue } from '../../types/siteEvidence';
import { EXTRACTION_RELEVANCE_FLOOR } from '../docMatching/relevanceThresholds';
import { isFieldFilled } from '../../types/authoredEvidence';

/** Scope the utility/queue completeness view without changing the contextual SED.
 * A section link alone cannot promote all its provider leaves to document facts.
 * Old, exclusively document-extracted sections remain usable when a live linked
 * document has an explicit requirement witness; modern mixed sections need the
 * exact field ledger. Authored evidence is handled before this view is used.
 */
export function scopeGridRequirementEvidence(args: {
  requirement: ChecklistItem;
  sed: SiteEvidenceDocument;
  documents: ReadonlyArray<SiteDocument>;
  match?: RankedChecklistMatch;
}): SiteEvidenceDocument {
  const { requirement, sed, documents, match } = args;
  const live = new Map<string, SiteDocument>();
  const counts = new Map<string, number>();
  for (const document of documents) {
    counts.set(document.id, (counts.get(document.id) ?? 0) + 1);
    live.set(document.id, document);
  }
  const attached = (id: string): boolean => counts.get(id) === 1;
  const relevant = (id: string): boolean => {
    if (!attached(id)) return false;
    const document = live.get(id)!;
    return (Array.isArray(document.topics?.requirementIds) && document.topics.requirementIds.includes(requirement.id))
      || (Array.isArray(document.topicSegments) && document.topicSegments.some(segment =>
        Array.isArray(segment?.requirementIds) && segment.requirementIds.includes(requirement.id)))
      || match?.matched.get(requirement.id)?.some(row => row.docId === id
        && Number.isFinite(row.score) && row.score >= EXTRACTION_RELEVANCE_FLOOR) === true;
  };
  const sameValue = (a: unknown, b: unknown): boolean => a !== undefined && a !== null
    && JSON.stringify(a) === JSON.stringify(b);
  const result = { ...sed };
  for (const sectionId of REQUIREMENT_TO_SECTION_MAP[requirement.id] ?? []) {
    const slot = sed[sectionId] as EvidenceSection<unknown> | undefined;
    if (!slot) continue;
    const detail = slot.provenanceDetail;
    const links = [...(slot.linkedDocumentIds ?? []), ...(slot.linkedDocumentRefs ?? []).map(row => row.docId)];
    const hasModernLedger = Object.keys(slot.docFieldSources ?? {}).length > 0
      || Object.keys(detail?.autoFillFieldSources ?? {}).length > 0;
    const legacyDocumentSection = slot.status === 'verified-from-doc'
      && (slot.provenance === undefined || slot.provenance === 'doc')
      && !hasModernLedger && links.some(relevant);
    const originalData = slot.data && typeof slot.data === 'object'
      ? slot.data as Record<string, unknown> : {};
    const data: Record<string, unknown> = legacyDocumentSection ? { ...originalData } : {};
    for (const [key, source] of Object.entries(slot.docFieldSources ?? {})) {
      if (relevant(source.docId) && source.attribution?.appliesToProject !== 'other-project'
        && sameValue(originalData[key], source.value)) data[key] = originalData[key];
    }
    const autoFillFields: Record<string, StructuredEvidenceValue> = {};
    const autoFillFieldSources: NonNullable<NonNullable<typeof detail>['autoFillFieldSources']> = {};
    for (const field of requirement.authoredSchema ?? []) {
      if (detail?.fieldReviews?.[field.key]?.decision === 'rejected') continue;
      const value = detail?.autoFillFields?.[field.key];
      const source = detail?.autoFillFieldSources?.[field.key];
      if (source?.requirementId === requirement.id && attached(source.docId)
        && source.attribution?.appliesToProject !== 'other-project' && sameValue(value, source.value)) {
        autoFillFields[field.key] = value!;
        autoFillFieldSources[field.key] = source;
      } else if (legacyDocumentSection && relevant(detail?.docId ?? '') && value !== undefined) {
        autoFillFields[field.key] = value;
      }
    }
    // The utility worksheet also exposes source-backed topology/pipeline
    // observations. Even many such observations are not a utility statement.
    const hasUtilityStatement = requirement.id !== 'utility-letter'
      || isFieldFilled(data.utilityName)
      || ['utilityName', 'capacityOffered_MW', 'serviceDate', 'interconnectionClass', 'costEstimate_USD']
        .some(key => isFieldFilled(autoFillFields[key]));
    if (!hasUtilityStatement) {
      for (const key of Object.keys(data)) delete data[key];
      for (const key of Object.keys(autoFillFields)) delete autoFillFields[key];
    }
    const hasEvidence = Object.keys(data).length > 0 || Object.keys(autoFillFields).length > 0;
    const scoped: EvidenceSection<unknown> = {
      ...slot,
      data: hasEvidence ? data : null,
      status: hasEvidence ? 'verified-from-doc' : 'unavailable',
      linkedDocumentIds: links.filter(relevant),
      linkedDocumentRefs: slot.linkedDocumentRefs?.filter(row => relevant(row.docId)),
      provenanceDetail: { ...detail,
        mergeReason: detail?.mergeReason ?? 'Derived requirement-specific document completeness view',
        autoFillFields, autoFillFieldSources },
    };
    // Section-specific payloads share the EvidenceSection envelope at runtime.
    (result as unknown as Record<string, unknown>)[sectionId] = scoped;
  }
  return result;
}
